import { useEffect, useState, useRef } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Upload, Download, FolderPlus, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { RevenueDialog } from "@/components/RevenueDialog";
import { ProductTree } from "@/components/ProductTree";
import { CategoryDialog } from "@/components/CategoryDialog";
import { useAuth } from "@/context/AuthContext";
import { canViewRevenue, isAdmin } from "@/lib/access";

const emptyForm = { name: "", sku: "", category: "Genel", points: 0, is_active: true, catalog_id: "" };

export default function Products() {
  const [items, setItems] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState(new Set());
  const [revenueFor, setRevenueFor] = useState(null);
  const { user } = useAuth();
  const showRev = canViewRevenue(user);
  const importRef = useRef();

  const load = () => http.get("/products").then((r) => { setItems(r.data); setSelected(new Set()); });
  const loadCatalogs = () => http.get("/catalogs").then((r) => setCatalogs(r.data));
  useEffect(() => { load(); loadCatalogs(); }, []);

  // Hiyerarşik path üret (örn: Mobil / Fatura / Kurumsal)
  const catalogPath = (id) => {
    if (!id) return "";
    const map = Object.fromEntries(catalogs.map((c) => [c.id, c]));
    const parts = [];
    let cur = map[id];
    let guard = 0;
    while (cur && guard < 10) {
      parts.unshift(cur.name);
      cur = cur.parent_id ? map[cur.parent_id] : null;
      guard++;
    }
    return parts.join(" / ");
  };
  const [cat, setCat] = useState(null);
  const saveCat = async (f) => {
    if (!f.name.trim()) { toast.error("Kategori adı zorunlu"); return; }
    const body = { name: f.name.trim(), parent_id: f.parent_id || null, color: f.color, icon: f.icon || "Folder", order: f.order || 0 };
    try {
      if (f.id) await http.put(`/catalogs/${f.id}`, body); else await http.post("/catalogs", body);
      toast.success("Kategori kaydedildi"); setCat(null); loadCatalogs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };
  const removeCat = async (c) => {
    if (!window.confirm(`"${c.name}" ve alt kategorileri silinsin mi? İçindeki ürünler silinmez, kategorisiz kalır.`)) return;
    await http.delete(`/catalogs/${c.id}`); toast.success("Kategori silindi"); loadCatalogs(); load();
  };

  const openNew = (catalog_id = "") => { setEditing(null); setForm({ ...emptyForm, catalog_id: typeof catalog_id === "string" ? catalog_id : "" }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p, catalog_id: p.catalog_id || "" }); setOpen(true); };

  const downloadTemplate = () => {
    const csv = "sku,name,category,points,is_active\nTRF-SAMPLE,Örnek Ürün,İnternet,50,true\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "urun_sablonu.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await http.post("/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} eklendi, ${data.updated} güncellendi${data.errors?.length ? ` (${data.errors.length} hata)` : ""}`);
      if (data.errors?.length) console.warn("CSV errors:", data.errors);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İçe aktarım hatası");
    } finally {
      if (importRef.current) importRef.current.value = "";
    }
  };

  const xlsxRef = useRef(null);
  const exportXlsx = async () => {
    try {
      const res = await http.get("/catalog-transfer/export", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = "kategori_urun_aktarim.xlsx"; a.click();
      toast.success("Kategori ve ürünler Excel'e aktarıldı");
    } catch { toast.error("Dışa aktarılamadı"); }
  };
  const importXlsx = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: d } = await http.post("/catalog-transfer/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Kategori: ${d.categories_created} yeni, ${d.categories_updated} güncel · Ürün: ${d.products_created} yeni, ${d.products_updated} güncel${d.errors.length ? ` · ${d.errors.length} hata` : ""}`, { duration: 6000 });
      if (d.errors.length) console.warn("Import errors:", d.errors);
      load(); loadCatalogs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Yükleme hatası");
    } finally {
      if (xlsxRef.current) xlsxRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.name || !form.sku) { toast.error("Ad ve SKU zorunlu"); return; }
    if (!form.catalog_id) { toast.error("Kategori seçimi zorunlu"); return; }
    try {
      const payload = { ...form, points: Number(form.points), catalog_id: form.catalog_id || null };
      if (editing) await http.put(`/products/${editing.id}`, payload);
      else await http.post("/products", payload);
      toast.success("Kaydedildi");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const remove = async (p) => {
    await http.delete(`/products/${p.id}`);
    toast.success("Silindi"); load();
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    try {
      const { data } = await http.post("/products/bulk-delete", { ids });
      toast.success(`${data.deleted} ürün silindi`);
      load();
    } catch { toast.error("Silinemedi"); }
  };

  const toggleSelect = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (filtered.length > 0 && selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((p) => p.id)));
  };

  const filtered = items.filter((p) => !query ||
    p.name.toLowerCase().includes(query.toLowerCase()) ||
    p.sku.toLowerCase().includes(query.toLowerCase()) ||
    (p.category || "").toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="space-y-6">
      <header className="animate-fade-up flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Ürünler</h1>
          <p className="text-slate-400 text-sm mt-1">{items.length} ürün kayıtlı</p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Input placeholder="Ara..." value={query} onChange={(e) => setQuery(e.target.value)}
                 className="bg-slate-900/60 border-slate-700 w-full sm:w-56" data-testid="product-filter" />
          <Button variant="outline" onClick={toggleAll} className="border-slate-700 bg-slate-800/50" data-testid="product-select-all">
            {filtered.length > 0 && selected.size === filtered.length ? "Seçimi Kaldır" : "Tümünü Seç"}
          </Button>
          {selected.size > 0 && (
            <Button variant="destructive" onClick={bulkDelete} className="bg-red-600 hover:bg-red-500" data-testid="product-bulk-delete">
              <Trash2 size={14} className="mr-1" /> {selected.size} Sil
            </Button>
          )}
          <Button variant="outline" onClick={downloadTemplate} className="border-slate-700 bg-slate-800/50" data-testid="product-template">
            <Download size={14} className="mr-1" /> Şablon
          </Button>
          <Button variant="outline" onClick={() => importRef.current?.click()} className="border-slate-700 bg-slate-800/50" data-testid="product-import">
            <Upload size={14} className="mr-1" /> CSV İçe Aktar
          </Button>
          <input ref={importRef} type="file" accept=".csv,text/csv" className="hidden" onChange={uploadCsv} data-testid="product-import-input" />
          <Button variant="outline" onClick={exportXlsx} className="border-emerald-500/40 text-emerald-200 bg-slate-800/50" data-testid="catalog-transfer-export">
            <FileSpreadsheet size={14} className="mr-1" /> Excel'e Aktar
          </Button>
          <Button variant="outline" onClick={() => xlsxRef.current?.click()} className="border-emerald-500/40 text-emerald-200 bg-slate-800/50" data-testid="catalog-transfer-import">
            <Upload size={14} className="mr-1" /> Excel'den Yükle
          </Button>
          <input ref={xlsxRef} type="file" accept=".xlsx" className="hidden" onChange={importXlsx} data-testid="catalog-transfer-input" />
          <Button variant="outline" onClick={() => setCat({ name: "", parent_id: null, color: "#3b82f6", icon: "Folder", order: 0 })} className="border-blue-500/40 text-blue-200" data-testid="category-new">
            <FolderPlus size={15} className="mr-1" /> Yeni Kategori
          </Button>
          <Button onClick={() => openNew()} className="bg-blue-600 hover:bg-blue-500" data-testid="product-new">
            <Plus size={16} className="mr-1" /> Yeni Ürün
          </Button>
        </div>
      </header>

      <div className="animate-fade-up delay-100">
        <ProductTree catalogs={catalogs} products={filtered}
                     a={{ selected, toggleSelect, openEdit, remove, showRev, admin: isAdmin(user), setRevenueFor, filtering: !!query,
                          newCat: (parent_id) => setCat({ name: "", parent_id, color: "#3b82f6", icon: "Folder", order: 0 }),
                          editCat: (c) => setCat({ ...c }), removeCat, newProduct: openNew }} />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle>{editing ? "Ürün Düzenle" : "Yeni Ürün"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Ad</Label>
              <textarea rows={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="w-full rounded-md bg-slate-950/50 border border-slate-700 px-3 py-2 text-sm resize-none" data-testid="product-form-name" />
            </div>
            <div>
              <Label>Kategori <span className="text-red-400">*</span></Label>
              <select
                value={form.catalog_id || ""}
                onChange={(e) => setForm({ ...form, catalog_id: e.target.value })}
                className="w-full h-10 bg-slate-950/50 border border-slate-700 rounded-md px-3 text-sm"
                data-testid="product-form-catalog"
              >
                <option value="">— Kategori seçin —</option>
                {catalogs.map((c) => (
                  <option key={c.id} value={c.id}>{catalogPath(c.id)}</option>
                ))}
              </select>
              {catalogs.length === 0 && (
                <p className="text-xs text-amber-400 mt-1">Önce "Yeni Kategori" ile bir kategori oluşturun.</p>
              )}
            </div>
            <div>
              <Label>SKU</Label>
              <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })}
                     className="bg-slate-950/50 border-slate-700 font-mono" data-testid="product-form-sku" />
            </div>
            <div>
              <Label>Puan</Label>
              <Input type="number" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })}
                     className="bg-slate-950/50 border-slate-700" data-testid="product-form-points" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Aktif</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="product-form-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-700">İptal</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-500" data-testid="product-form-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {cat && <CategoryDialog value={cat} catalogs={catalogs} pathOf={catalogPath} onClose={() => setCat(null)} onSave={saveCat} />}
      <RevenueDialog product={revenueFor} onClose={() => setRevenueFor(null)} onSaved={load} />
    </div>
  );
}
